// Mirrors com.fivtyonetrips.colab.domain.model.InvitationCode from 51trips-colab-api,
// as returned by GET /invitation-codes and accepted by POST /invitation-codes/{id}.

export type InvitationCodeItinerary = {
    name?: string
    startDate?: number
    endDate?: number
    tripImageUrl?: string
    // Traveler-facing itinerary attachment: either uploaded (Firebase Storage
    // download URL) or a link pasted straight from wherever the agency already
    // hosts it — same field either way, see AssignInvitationCodeDialog/
    // EditInvitationCodeDialog.
    tripItineraryPdfUrl?: string
    countryCodes?: string[]
}

export type InvitationCodeTraveler = {
    firstName?: string
    lastName?: string
    email?: string
    // The read model (PackTraveler) already carries a traveler phone, so the
    // write side accepts one too — "Asignar código a un viajero" collects it.
    phone?: string
}

// Who will upload the photos of a "same TripBook, printed several times" code:
// the agency itself, or another person (whose details go in mainTraveler).
export type PhotoUploader = 'AGENCY' | 'OTHER'

export type InvitationCodeRecord = {
    id: string
    agencyID?: string
    code?: string
    partnerOrderID?: string
    observations?: string
    itinerary?: InvitationCodeItinerary
    mainTraveler?: InvitationCodeTraveler
    isDelivered?: boolean
    status?: string
    // Reusable, agency-wide codes (never tied to a single traveler/trip) — a free
    // one (unlimitedRedemption) and/or a paid one (stripeProductCode set). These
    // must be surfaced as the agency's generic-code banner, not as pending rows
    // in "Códigos prerellenados".
    unlimitedRedemption?: boolean
    stripeProductCode?: string | null
    // "Varios TripBooks" (AssignInvitationCodeDialog): codes sharing a trip, one
    // per traveler, carry the same groupId; a single code printed several times
    // carries copies + photoUploader instead.
    groupId?: string
    copies?: number
    photoUploader?: PhotoUploader
    // TripBook pages this code covers — chosen among the agency's packs when it
    // has several paginations (AssignInvitationCodeDialog). Codes created
    // before it was stored don't carry it: read them as the agency's default.
    numberOfPagesIncluded?: number
    // A shared album's "Lista de viajeros" — one per printed copy, never more
    // than `copies`.
    travelers?: InvitationCodeTraveler[]
    // The gift PDF ("ebook") for this code. Generated in the background after
    // every write, unless the write asked for it in the same request (SyncEbookOption).
    ebookUrl?: string | null
}

// Asks the backend to generate the gift PDF within the same request (?ebook=sync)
// so it comes back in `ebookUrl` — AssignInvitationCodeDialog offers it for
// download on its success screen. Not sent in the body.
export type SyncEbookOption = {syncEbook?: boolean}

// partnerOrderID is @NotBlank on the backend — always resend the record's current value,
// since this endpoint replaces itinerary/observations/partnerOrderID/traveler/isDelivered
// wholesale (it's not a partial patch): omitting a field clears it server-side.
export type UpdateInvitationCodePayload = {
    id: string
    partnerOrderID: string
    agencyID?: string
    observations?: string
    itinerary?: InvitationCodeItinerary
    traveler?: InvitationCodeTraveler
    isDelivered?: boolean
    copies?: number
    photoUploader?: PhotoUploader
    travelers?: InvitationCodeTraveler[]
    numberOfPagesIncluded?: number
}

// POST /invitation-codes/group — `quantity` codes for the same trip under one
// groupId. The free pool codes in `invitationCodeIds` are consumed first (in
// order); the backend only mints the ones still missing.
export type AssignInvitationCodeGroupPayload = {
    agencyID: string
    itinerary: InvitationCodeItinerary
    quantity: number
    invitationCodeIds: string[]
    numberOfPagesIncluded?: number
}

function toSafeItineraryPayload(itinerary?: InvitationCodeItinerary): InvitationCodeItinerary | undefined {
    if (!itinerary || !itinerary.name || itinerary.startDate == null || itinerary.endDate == null) return undefined
    return {
        name: itinerary.name,
        countryCodes: itinerary.countryCodes ?? [],
        startDate: itinerary.startDate,
        endDate: itinerary.endDate,
        tripImageUrl: itinerary.tripImageUrl,
        tripItineraryPdfUrl: itinerary.tripItineraryPdfUrl,
    }
}

function toSafeTravelerPayload(traveler?: InvitationCodeTraveler): InvitationCodeTraveler | undefined {
    if (!traveler || (!traveler.firstName && !traveler.lastName && !traveler.email && !traveler.phone)) return undefined
    return {firstName: traveler.firstName, lastName: traveler.lastName, email: traveler.email, phone: traveler.phone}
}

// Builds a full update payload from the record's current state, applying only the
// given overrides — every other field is resent unchanged so it isn't clobbered.
export function buildInvitationCodeUpdatePayload(
    record: InvitationCodeRecord,
    overrides: Partial<Pick<UpdateInvitationCodePayload, 'itinerary' | 'traveler' | 'isDelivered' | 'copies' | 'photoUploader' | 'travelers' | 'numberOfPagesIncluded'>>
): UpdateInvitationCodePayload {
    return {
        id: record.id,
        partnerOrderID: record.partnerOrderID ?? '',
        agencyID: record.agencyID,
        observations: record.observations,
        itinerary: overrides.itinerary ?? toSafeItineraryPayload(record.itinerary),
        traveler: overrides.traveler ?? toSafeTravelerPayload(record.mainTraveler),
        isDelivered: overrides.isDelivered ?? record.isDelivered,
        copies: overrides.copies ?? record.copies,
        photoUploader: overrides.photoUploader ?? record.photoUploader,
        travelers: overrides.travelers ?? record.travelers,
        numberOfPagesIncluded: overrides.numberOfPagesIncluded ?? record.numberOfPagesIncluded,
    }
}
